// Admin "all requests" — screen 1 of the admin console task. Ledger-style:
// one ruled table, no card-per-row, no sidebar (root CLAUDE.md), same shape
// as ApprovalQueuePage/QueuePage. Lists EVERY request (not just one sales
// user's queue) with ref_no, organization, status, assignee, and age, with
// an inline assign/reassign control per row.
//
// Assign/reassign uses the SAME endpoint (POST /admin/requests/:id/assign —
// see hooks/useAdminRequests.js's own comment) so this screen's "assign" and
// "reassign" are one interaction: a plain <select> per row, already showing
// the current assignee, that fires the mutation on change. No custom picker
// component — admin/CLAUDE.md: hand-roll, no data-grid library, and a
// one-field picker does not earn a whole component.
//
// A terminal-status row (sent/closed/cancelled) cannot be (re)assigned
// server-side (REQUEST_NOT_ASSIGNABLE) — those rows render the assignee as
// plain text instead of offering a control that would only ever 409.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext.jsx';
import {
  useAdminRequests,
  useAdminSalesUsers,
  useAssignRequest,
  useProformaTurnaroundMetric,
} from '../hooks/useAdminRequests.js';
import LoadingState from '../components/LoadingState.jsx';
import ErrorState from '../components/ErrorState.jsx';
import EmptyState from '../components/EmptyState.jsx';
import { statusLabel } from '../i18n.js';
import { formatDate } from '../utils/formatDate.js';
import { useActionFeedback } from '../hooks/useActionFeedback.js';
import { useSortableList } from 'shared/hooks/useSortableList.js';
import { SortHeader } from 'shared/components/SortHeader/index.js';
import styles from './AllRequestsPage.module.css';

// A request in one of these statuses is done — requestService.js's own
// TERMINAL_STATUSES set, mirrored here (that set isn't exported for the
// frontend to import, and duplicating three literals is simpler than adding
// a shared export for a one-screen need).
const TERMINAL_STATUSES = new Set(['sent', 'closed', 'cancelled']);

// Display rounding for the turnaround stat: one decimal place under 10
// hours (so a fast shop doesn't get flattened to a misleading "0h"), a
// whole number above that — never a long float.
function formatTurnaroundHours(avgHours) {
  return avgHours < 10 ? avgHours.toFixed(1) : Math.round(avgHours).toString();
}

// key: the row field to sort on; numeric: compare as numbers, not strings —
// same shape as AllProformasPage.jsx's own SORT_COLUMNS.
const SORT_COLUMNS = {
  ref: { key: 'ref_no' },
  organization: { key: 'organization_name' },
  status: { key: 'status' },
  assignee: { key: 'assigned_to_name' },
  age: { key: 'created_at' },
};

const STATUS_FILTERS = [
  'new',
  'assigned',
  'pricing',
  'pending_approval',
  'approved',
  'sent',
  'closed',
  'cancelled',
];

export default function AllRequestsPage() {
  const { t, lang } = useLanguage();
  const [statusFilter, setStatusFilter] = useState('all');

  const { data: requests, isLoading, isError, error, refetch } = useAdminRequests();
  const { data: salesUsers, isLoading: isSalesUsersLoading } = useAdminSalesUsers();
  const turnaroundQuery = useProformaTurnaroundMetric();
  const assignMutation = useAssignRequest();
  const { toastOk, toastErr } = useActionFeedback();
  // Default 'age' desc mirrors the server's own default order
  // (requestRepository.js#list: created_at DESC, newest-submitted-first).
  const { sortCol, sortDir, handleSort, sortRows } = useSortableList(SORT_COLUMNS, 'age', 'desc');

  // Server-side INVALID_ASSIGNEE already excludes inactive/non-sales
  // accounts (400), but offering one in the picker at all would just be a
  // guaranteed bounce — filtered here so every option in the dropdown is a
  // real, assignable candidate.
  const activeSalesUsers = (salesUsers || []).filter((user) => user.is_active);

  const allRequests = requests || [];
  const filteredRequests = sortRows(
    statusFilter === 'all' ? allRequests : allRequests.filter((r) => r.status === statusFilter)
  );

  const isFilteredEmpty =
    !isLoading && !isError && filteredRequests.length === 0 && allRequests.length > 0;
  const isTrulyEmpty =
    !isLoading && !isError && filteredRequests.length === 0 && allRequests.length === 0;

  const handleAssign = (requestId, rawAssignedTo) => {
    if (!rawAssignedTo) return; // placeholder re-selected, not a real choice
    const assignedTo = Number(rawAssignedTo);
    assignMutation.mutate(
      { requestId, assignedTo },
      {
        onSuccess: () => toastOk(t('allRequests.assignedToast')),
        onError: (err) =>
          toastErr(err, ['INVALID_ASSIGNEE', 'REQUEST_NOT_ASSIGNABLE', 'REQUEST_NOT_FOUND']),
      }
    );
  };

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{t('allRequests.title')}</h1>
      <p className={styles.subtitle}>{t('allRequests.subtitle')}</p>

      <p className={`${styles.turnaround} tabularNums`}>
        {turnaroundQuery.isLoading && t('allRequests.turnaround.loading')}
        {turnaroundQuery.isError && t('allRequests.turnaround.error')}
        {!turnaroundQuery.isLoading &&
          !turnaroundQuery.isError &&
          turnaroundQuery.data &&
          (turnaroundQuery.data.sampleSize > 0
            ? t('allRequests.turnaround.summary', {
                hours: formatTurnaroundHours(turnaroundQuery.data.avgHours),
                count: turnaroundQuery.data.sampleSize,
              })
            : t('allRequests.turnaround.noData'))}
      </p>
      <hr className="hairline" />

      <div className={styles.filterRow}>
        <label className={styles.filterLabel} htmlFor="allRequests-statusFilter">
          {t('allRequests.filter.label')}
        </label>
        <select
          id="allRequests-statusFilter"
          className={styles.filterSelect}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">{t('allRequests.filter.all')}</option>
          {STATUS_FILTERS.map((status) => (
            <option key={status} value={status}>
              {statusLabel(status, lang)}
            </option>
          ))}
        </select>
      </div>

      {isLoading && <LoadingState variant="rows" rows={6} />}

      {isError && !isLoading && (
        <ErrorState messageId="allRequests.errorBody" error={error} onRetry={refetch} />
      )}

      {isTrulyEmpty && (
        <EmptyState titleId="allRequests.emptyTitle" bodyId="allRequests.emptyBody" />
      )}

      {isFilteredEmpty && (
        <EmptyState
          titleId="allRequests.emptyFilteredTitle"
          bodyId="allRequests.emptyFilteredBody"
        />
      )}

      {!isLoading && !isError && filteredRequests.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr className={styles.headerRow}>
              <SortHeader
                column="ref"
                label={t('allRequests.col.ref')}
                sortCol={sortCol}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <SortHeader
                column="organization"
                label={t('allRequests.col.organization')}
                sortCol={sortCol}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <SortHeader
                column="status"
                label={t('allRequests.col.status')}
                sortCol={sortCol}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <SortHeader
                column="assignee"
                label={t('allRequests.col.assignee')}
                sortCol={sortCol}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <SortHeader
                column="age"
                label={t('allRequests.col.age')}
                sortCol={sortCol}
                sortDir={sortDir}
                onSort={handleSort}
              />
            </tr>
          </thead>
          <tbody>
            {filteredRequests.map((request) => {
              const isTerminal = TERMINAL_STATUSES.has(request.status);
              const isThisRowPending =
                assignMutation.isPending && assignMutation.variables?.requestId === request.id;

              return (
                <tr key={request.id} className={styles.row}>
                  <td className={`${styles.refCell} tabularNums`}>
                    <Link
                      to={`/all-requests/${request.id}`}
                      className={styles.refLink}
                      aria-label={t('allRequests.openDetail')}
                    >
                      {request.ref_no}
                    </Link>
                  </td>
                  <td>
                    {lang === 'am' && request.organization_name_am
                      ? request.organization_name_am
                      : request.organization_name}
                  </td>
                  <td>{statusLabel(request.status, lang)}</td>
                  <td>
                    {isTerminal ? (
                      <span>{request.assigned_to_name || t('allRequests.unassigned')}</span>
                    ) : (
                      <div className={styles.assignCell}>
                        <select
                          className={styles.assignSelect}
                          value={request.assigned_to || ''}
                          disabled={isThisRowPending || isSalesUsersLoading}
                          onChange={(e) => handleAssign(request.id, e.target.value)}
                          aria-label={t('allRequests.assignLabel')}
                        >
                          <option value="">{t('allRequests.unassigned')}</option>
                          {activeSalesUsers.map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.full_name}
                            </option>
                          ))}
                        </select>
                        {isThisRowPending && (
                          <span className={styles.assigning}>{t('allRequests.assigning')}</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="tabularNums">{formatDate(request.created_at, lang) || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
